import { useState } from 'react';
import { Building2, CheckCircle2, LoaderCircle, Search, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';

const label = value => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, character => character.toUpperCase());

function formatAddress(address) {
  if (!address || typeof address !== 'object') return 'Address not supplied';
  return [address.premises, address.address_line_1, address.address_line_2, address.locality, address.region, address.postal_code, address.country]
    .filter(Boolean)
    .join(', ');
}

export default function CompaniesHouseOrganisationLookup({ selectedCompanyNumber = '', onSelect, manualMode = false, onModeChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState('');
  const [error, setError] = useState('');

  const search = async () => {
    const term = query.trim();
    if (term.length < 2) {
      setError('Enter at least two characters of the company name or company number.');
      return;
    }
    setSearching(true);
    setError('');
    try {
      const response = await api(`/api/admin/partnerships/companies-house/search?q=${encodeURIComponent(term)}`);
      setResults(Array.isArray(response?.data) ? response.data : []);
    } catch (caught) {
      setResults([]);
      setError(caught?.message || 'Companies House search could not be completed.');
    } finally {
      setSearching(false);
    }
  };

  const choose = async companyNumber => {
    setLoadingCompany(companyNumber);
    setError('');
    try {
      const response = await api(`/api/admin/partnerships/companies-house/company/${encodeURIComponent(companyNumber)}`);
      if (!response?.company) throw new Error('Companies House did not return a company profile.');
      onSelect?.(response.company);
      onModeChange?.(false);
    } catch (caught) {
      setError(caught?.message || 'The Companies House company profile could not be loaded.');
    } finally {
      setLoadingCompany('');
    }
  };

  if (manualMode) {
    return <div className="companies-house-lookup manual wide">
      <div className="companies-house-heading"><Building2 size={18}/><div><strong>Manual organisation identity</strong><small>Use this for organisations that are not registered companies at Companies House, including some charities, public bodies, overseas organisations and sole traders.</small></div></div>
      <button type="button" className="button secondary small govuk-button govuk-button--secondary" onClick={() => onModeChange?.(false)}>Search Companies House instead</button>
    </div>;
  }

  return <div className="companies-house-lookup wide">
    <div className="companies-house-heading"><ShieldCheck size={18}/><div><strong>Find the organisation at Companies House</strong><small>Search the official register and select the exact legal entity. Sousa Murray will save a Companies House snapshot into the partnership record.</small></div></div>
    <div className="companies-house-search-row">
      <div className="companies-house-search-input"><Search size={16}/><input className="govuk-input" value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); search(); } }} placeholder="Company name or company number"/></div>
      <button type="button" className="button secondary govuk-button govuk-button--secondary" disabled={searching} onClick={search}>{searching ? <LoaderCircle className="spin" size={15}/> : <Search size={15}/>}Search</button>
    </div>
    {error && <div className="companies-house-error">{error}</div>}
    {selectedCompanyNumber && <div className="companies-house-selected"><CheckCircle2 size={16}/><span>Selected Companies House company <strong>{selectedCompanyNumber}</strong>. The official profile will be re-checked when the onboarding record is created.</span></div>}
    {results.length > 0 && <div className="companies-house-results">{results.map(company => <button type="button" key={company.company_number} className={selectedCompanyNumber === company.company_number ? 'selected' : ''} onClick={() => choose(company.company_number)} disabled={Boolean(loadingCompany)}>
      <Building2 size={18}/><span><strong>{company.company_name}</strong><small>{company.company_number} · {label(company.company_status)} · {label(company.company_type)}</small><small>{company.address_snippet || formatAddress(company.address)}</small></span>{loadingCompany === company.company_number ? <LoaderCircle className="spin" size={17}/> : selectedCompanyNumber === company.company_number ? <CheckCircle2 size={17}/> : null}
    </button>)}</div>}
    {!searching && query.trim().length >= 2 && results.length === 0 && !error && <p className="companies-house-empty govuk-body">No companies returned for that search.</p>}
    <button type="button" className="companies-house-manual-link" onClick={() => onModeChange?.(true)}>This organisation is not registered at Companies House</button>
  </div>;
}
